

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBreakReport,
  exportBreakReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Clock, Coffee, User, Filter, X, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';

const BreakReportPage = () => {
  const dispatch = useDispatch();

  const {
    breakReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  // Get employees from Redux state
  const employees = useSelector((state) => state.common?.employees?.items || []);

  // Get loading state for employees
  const employeesLoading = useSelector((state) => state.common?.employees?.loading || false);

  const summary = breakReport.data.summary || {};
  const results = breakReport.data.results || [];
  const pagination = breakReport.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);

  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    employee: 'all',
    title: 'all',
    shift_type: 'all',
    period_start: '',
    period_end: '',
    break_not_availed: '',
  });

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value =>
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'break',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.employee !== 'all' && { employee: advancedFilters.employee }),
      ...(advancedFilters.title !== 'all' && { title: advancedFilters.title }),
      ...(advancedFilters.shift_type !== 'all' && { shift_type: advancedFilters.shift_type }),
      ...(advancedFilters.break_not_availed && {
        break_not_availed: advancedFilters.break_not_availed,
      }),
      ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
      ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
    };

    dispatch(fetchBreakReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all employees on component mount
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      employee: 'all',
      title: 'all',
      break_not_availed: "",
      shift_type: 'all',
      period_start: '',
      period_end: '',
    });
  };

  const columns = [
    {
      accessorKey: 'employee_code',
      header: 'Employee Code',
    },
    {
      accessorKey: 'employee_name',
      header: 'Employee Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span>{row.original.employee_name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Break Type',
      cell: ({ row }) => {
        const title = row.original.title || 'Custom Break';
        return (
          <Badge variant={title.includes('Not On Desk') ? 'destructive' : 'secondary'}>
            {title || 'Untitled'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'break_not_availed',
      header: 'Break Unavailed Type',
      cell: ({ row }) => row.original.break_not_availed ?? '-'
    },
    {
      accessorKey: 'punch_in',
      header: 'Punch In',
      cell: ({ row }) => (formatDateTime(row.original.punch_in) ?? '-')
    },
    {
      accessorKey: 'punch_out',
      header: 'Punch Out',
      cell: ({ row }) => (formatDateTime(row.original.punch_out) ?? '-')
    },

    {
      accessorKey: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <Badge variant={row.original.duration === '0m' ? 'outline' : 'default'}>
          {row.original.duration}
        </Badge>
      ),
    },
    {
      accessorKey: 'not_on_desk_reason',
      header: 'Reason',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.not_on_desk_reason || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'shift_type',
      header: 'Shift',
      cell: ({ row }) => (
        <Badge variant={row.original.shift_type === 'Day' ? 'default' : 'secondary'}>
          {row.original.shift_type}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Break Time Report"
        description="Detailed log of all employee breaks, including duration and reasons"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="employee">Employee</Label>
                <Select
                  value={advancedFilters.employee}
                  onValueChange={(value) => handleAdvancedFilterChange('employee', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Break Title</Label>
                <Select
                  value={advancedFilters.title}
                  onValueChange={(value) => handleAdvancedFilterChange('title', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                <SelectContent>
  <SelectItem value="all">All</SelectItem>
  <SelectItem value="morning_break">Morning Break</SelectItem>
  <SelectItem value="lunch_break">Lunch Break</SelectItem>
  <SelectItem value="evening_break">Evening Break</SelectItem>
  <SelectItem value="dinner_break">Dinner Break</SelectItem>
  <SelectItem value="not_on_desk_break">Not On Desk Break</SelectItem>
  <SelectItem value="attendance">Attendance</SelectItem>
</SelectContent>

                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="shift_type">Shift Type</Label>
                <Select
                  value={advancedFilters.shift_type}
                  onValueChange={(value) => handleAdvancedFilterChange('shift_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select shift type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Day">Day</SelectItem>
                    <SelectItem value="Night">Night</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              <FormField
                label="Employee"
                name="employee"
                type="select"
                value={advancedFilters.employee}
                onChange={(e) =>
                  handleAdvancedFilterChange('employee', e.target.value)
                }
                disabled={employeesLoading}
                options={[
                  { value: 'all', label: 'All' },
                  ...(Array.isArray(employees)
                    ? employees.map((employee) => ({
                      value: String(employee.id),
                      label: employee.name,
                    }))
                    : []),
                ]}
              />

              <FormField
                label="Break Title"
                name="title"
                type="select"
                value={advancedFilters.title}
                onChange={(e) =>
                  handleAdvancedFilterChange('title', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'morning_break', label: 'Morning Break' },
                  { value: 'lunch_break', label: 'Lunch Break' },
                  { value: 'evening_break', label: 'Evening Break' },
                  { value: 'dinner_break', label: 'Dinner Break' },
                  { value: 'break_not_availed', label: 'Break Not Availed' },
                  { value: 'not_on_desk_break', label: 'Not On Desk Break' },
                  // { value: 'attendance', label: 'Attendance' },
                ]}
              />

              {advancedFilters.title === 'break_not_availed' && (

                <FormField
                  label="Break Unavailed Type"
                  name="break_not_availed"
                  type="select"
                  value={advancedFilters.break_not_availed}
                  onChange={(e) =>
                    handleAdvancedFilterChange("break_not_availed", e.target.value)
                  }
                  options={[
                    { value: "", label: "All" },
                    { value: "all", label: "All Break Type" },
                    { value: "morning_break", label: "Morning Break" },
                    { value: "lunch_break", label: "Lunch Break" },
                    { value: "evening_break", label: "Evening Break" },
                    { value: "dinner_break", label: "Dinner Break" },
                  ]}
                />
              )}

              <FormField
                label="Shift Type"
                name="shift_type"
                type="select"
                value={advancedFilters.shift_type}
                onChange={(e) =>
                  handleAdvancedFilterChange('shift_type', e.target.value)
                }
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'day', label: 'Day' },
                  { value: 'night', label: 'Night' },
                ]}
              />


              {/* <div className="space-y-2">
                <Label>Period Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="period_start">Start Date</Label>
                    <Input
                      type="date"
                      placeholder="Start date"
                      value={advancedFilters.period_start}
                      onChange={(e) => handleAdvancedFilterChange('period_start', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="period_end">End Date</Label>
                    <Input
                      type="date"
                      placeholder="End date"
                      value={advancedFilters.period_end}
                      onChange={(e) => handleAdvancedFilterChange('period_end', e.target.value)}
                    />
                  </div>
                </div>
              </div> */}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Break Entries</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.count || 0}</div>
            <p className="text-xs text-muted-foreground">Across all employees</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Employees</CardTitle>
            <User className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(results.map(r => r.employee_code)).size}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Common Break</CardTitle>
            <Coffee className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {results.filter(r => r.title?.includes('Lunch') || r.title?.includes('Dinner')).length > 0 ? 'Meal Breaks' : 'Various'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading break time logs...</span>
        </div>
      ) : ( */}
      <DataTable
        data={results} // Use the paginated results from the server
        columns={columns}
        totalCount={pagination.count}
        searchPlaceholder="Search by employee code, name, reason..."
        onSearch={setSearchTerm}
        showSearch={true}
        pageIndex={page - 1} // Convert to 0-based index for DataTable
        pageSize={pageSize}
        pageCount={totalPages} // Use total pages from server response
        onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
        onPageSizeChange={(newSize) => setPageSize(newSize)}
        isLoading={isLoading}
        onExport={() =>
          dispatch(exportBreakReport({
            filters: {
              page,
              page_size: pageSize,
              search: searchTerm,
              report_type: 'break',
              ...dateFilters,
              // Include advanced filters in export
              ...(advancedFilters.employee !== 'all' && { employee: advancedFilters.employee }),
              ...(advancedFilters.title !== 'all' && { title: advancedFilters.title }),
              ...(advancedFilters.break_not_availed && {
                break_not_availed: advancedFilters.break_not_availed,
              }),
              ...(advancedFilters.shift_type !== 'all' && { shift_type: advancedFilters.shift_type }),
              ...(advancedFilters.period_start && { period_start: advancedFilters.period_start }),
              ...(advancedFilters.period_end && { period_end: advancedFilters.period_end }),
            }
          }))
        }
        showExport={true}
        showImport={false}
      />
      {/* )} */}
    </div>
  );
};

export default BreakReportPage;